import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { BaseCommand, ICommand } from '../../commands/ICommand';
import { SessionMetrics } from '../../../domains/analytics/services/SessionMetrics';

// Generic command for changing text
export class ChangeTextCommand extends BaseCommand {
  protected itemIds: string[];
  protected oldTexts: Map<string, string | undefined>;
  protected newText: string;
  protected field: string;
  protected updateItem: (id: string, field: string, text: string | undefined) => void;
  
  constructor(
    itemIds: string[],
    newText: string,
    field: string,
    oldTexts: Map<string, string | undefined>,
    updateItem: (id: string, field: string, text: string | undefined) => void,
    description?: string
  ) {
    super('Change Text', description || `Change ${field} to "${newText}"`);
    this.itemIds = itemIds;
    this.newText = newText;
    this.field = field;
    this.oldTexts = oldTexts;
    this.updateItem = updateItem;
  }
  
  execute(): void {
    this.itemIds.forEach(id => {
      this.updateItem(id, this.field, this.newText);
    });
    // Track text editing (aggregated + first-time)
    SessionMetrics.increment('text_edited');
    SessionMetrics.firstTime('text_edited');
  }
  
  undo(): void {
    this.itemIds.forEach(id => {
      const oldText = this.oldTexts.get(id);
      this.updateItem(id, this.field, oldText);
    });
  }
}

// Base text editing tool
export abstract class TextEditTool extends BaseTool {
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Edit Text',
      icon: '✏️',
      category: 'edit',
      description: 'Edit text properties',
      version: '1.0.0',
      
      requiresSelection: true,
      minSelection: 1,
      
      ui: {
        toolbar: true,
        contextMenu: true,
        propertyPanel: true
      },
      
      capabilities: {
        modifiesShapes: true,
        createsShapes: false,
        deletesShapes: false,
        modifiesConnectors: true,
        createsConnectors: false,
        deletesConnectors: false,
        requiresUserInput: true,
        supportsMultiSelect: true,
        supportsBatchOperations: true
      },
      
      shortcuts: [{
        key: 't',
        modifiers: [],
        description: 'Edit text'
      }, {
        key: 'F2',
        modifiers: [],
        description: 'Edit selected item text'
      }]
    };
  }
  
  canExecute(context: IToolContext): boolean {
    return context.selection.size > 0;
  }
  
  // Abstract methods that must be implemented by subclasses
  abstract getTextFields(): { name: string; label: string }[];
  abstract getItemText(itemId: string, field: string): string | undefined;
  abstract setItemText(itemId: string, field: string, text: string | undefined): void;
  abstract getSelectedItems(): string[];
  
  execute(params: { text: string; field: string }): ICommand | null {
    if (!this.context || !params.text || !params.field) return null;
    
    const selectedIds = this.getSelectedItems();
    if (selectedIds.length === 0) return null;
    
    // Store old texts
    const oldTexts = new Map<string, string | undefined>();
    selectedIds.forEach(id => {
      oldTexts.set(id, this.getItemText(id, params.field));
    });
    
    return new ChangeTextCommand(
      selectedIds,
      params.text,
      params.field,
      oldTexts,
      (id, field, text) => this.setItemText(id, field, text)
    );
  }
  
  getPropertyPanelComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const selectedItems = this.getSelectedItems();
      const fields = this.getTextFields();
      
      const firstItemId = selectedItems[0];
      const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
      
      React.useEffect(() => {
        if (firstItemId) {
          const values: Record<string, string> = {};
          fields.forEach(field => {
            values[field.name] = this.getItemText(firstItemId, field.name) || '';
          });
          setFieldValues(values);
        }
      }, [firstItemId]);
      
      return React.createElement('div', {
        style: { marginTop: '10px' }
      }, [
        React.createElement('h4', {
          key: 'title',
          style: { margin: '0 0 10px 0', fontSize: '14px' }
        }, 'Text Properties'),
        
        ...fields.map(field => 
          React.createElement('div', {
            key: field.name,
            style: { marginBottom: '10px' }
          }, [
            React.createElement('label', {
              key: `${field.name}-label`,
              style: { display: 'block', fontSize: '12px', marginBottom: '4px', color: '#666' }
            }, `${field.label}:`),
            React.createElement('input', {
              key: `${field.name}-input`,
              type: 'text',
              value: fieldValues[field.name] || '',
              onChange: (e: any) => setFieldValues({
                ...fieldValues,
                [field.name]: e.target.value
              }),
              onBlur: () => {
                const currentValue = this.getItemText(firstItemId, field.name);
                if (fieldValues[field.name] !== currentValue) {
                  onExecute({ text: fieldValues[field.name], field: field.name });
                }
              },
              onKeyDown: (e: any) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              },
              style: {
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '12px'
              }
            })
          ])
        ),
        
        selectedItems.length > 1 && React.createElement('div', {
          key: 'multi-warning',
          style: { fontSize: '11px', color: '#ff9800', marginTop: '8px' }
        }, `⚠️ Editing ${selectedItems.length} items`)
      ]);
    };
  }
}